/**
 * Facturation : à quelle date un client paie, et combien de fois il a payé.
 *
 * Fonctions pures — voir tests/billing.test.js.
 *
 * Le modèle est discret, pas continu. Un client paie à date fixe, une fois par
 * mois : le cumul est donc un compte de versements échus, jamais une fraction.
 * L'ancien calcul faisait `effectif × tarif × jours / 30,4375` et produisait des
 * montants correspondant à 5,95 versements — un chiffre qu'aucune facture n'a
 * jamais porté.
 */

/** Un client paie un jour du mois, de 1 à 31. */
export const MIN_PAYMENT_DAY = 1
export const MAX_PAYMENT_DAY = 31

/**
 * Jour de facturation d'un client.
 *
 * À défaut de saisie, le jour où il est devenu client : quelqu'un qui signe le
 * 14 sera très probablement facturé le 14. Ça évite d'imposer un champ de plus
 * pour retrouver ce que la date de début dit déjà.
 */
export function paymentDayOf(record) {
  const day = Number.parseInt(record?.paymentDay, 10)
  if (Number.isInteger(day) && day >= MIN_PAYMENT_DAY && day <= MAX_PAYMENT_DAY) return day
  if (record?.since) {
    const fromSince = Number.parseInt(record.since.slice(8, 10), 10)
    if (Number.isInteger(fromSince)) return fromSince
  }
  return MIN_PAYMENT_DAY
}

/** Le jour de facturation ramené à un mois donné : le 31 devient le 30, ou le 28 en février. */
export function billingDateIn(year, month, paymentDay) {
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(paymentDay, daysInMonth))
}

/**
 * Nombre de versements échus entre la date de début et maintenant, bornes
 * comprises.
 *
 * Le versement du jour même de la signature compte : on devient client en
 * payant. Ceux qui tombent avant la signature, non — un client qui signe le 15
 * et paie le 1er n'a rien versé le 1er de ce mois-là, son premier versement est
 * celui du mois suivant.
 *
 * @param {Date} now  injecté pour rendre la fonction testable
 * @returns {number|null} null si la date de début est inexploitable
 */
export function countPayments(since, paymentDay, now = new Date()) {
  if (!since) return null
  const start = new Date(`${since}T00:00:00`)
  if (Number.isNaN(start.getTime())) return null
  if (now < start) return 0

  const day = Math.min(Math.max(paymentDay, MIN_PAYMENT_DAY), MAX_PAYMENT_DAY)
  let count = 0
  let year = start.getFullYear()
  let month = start.getMonth()

  // Les dates de facturation croissent d'un mois à l'autre : dès qu'on dépasse
  // `now`, tout le reste est à venir.
  for (;;) {
    const date = billingDateIn(year, month, day)
    if (date > now) break
    if (date >= start) count += 1
    month += 1
    if (month > 11) {
      month = 0
      year += 1
    }
  }

  return count
}

/**
 * Prochaine date de facturation, strictement après `now`.
 *
 * @returns {Date|null}
 */
export function nextPaymentDate(since, paymentDay, now = new Date()) {
  if (!since) return null
  const start = new Date(`${since}T00:00:00`)
  if (Number.isNaN(start.getTime())) return null

  const day = Math.min(Math.max(paymentDay, MIN_PAYMENT_DAY), MAX_PAYMENT_DAY)
  const from = now > start ? now : start
  let year = from.getFullYear()
  let month = from.getMonth()

  for (let i = 0; i < 2; i++) {
    const date = billingDateIn(year, month, day)
    if (date > now && date >= start) return date
    month += 1
    if (month > 11) {
      month = 0
      year += 1
    }
  }
  return null
}
